"use client";

import Image from "next/image";
import { ReactNode } from "react";
import SceneCanvas from "@/features/lomdaEditorV2/components/canvas/SceneCanvas";
import { LomdaProvider, useSceneFit } from "@/features/lomdaEditorV2/hooks";
import type {
    LomdaContent,
    LomdaMetadata,
} from "@/features/lomdaEditorV2/lib/lomdaTypes";
import { cn } from "@/lib/utils";
import { RichTextToolbarProvider } from "../components/scene/richTextEditor/richTextToolbarContext";

type Props = {
    content: LomdaContent | null;
    metadata: LomdaMetadata | null;
    lomdaId: string;
    lomdaName: string;
    userId: string;
    className?: string;
    fallback?: ReactNode;
};

/**
 * Renders the first scene of a V2 lomda. Non-interactive — safe to embed in
 * cards, sheets, grids.
 */
export default function LomdaV2Thumbnail({
    content,
    metadata,
    lomdaId,
    lomdaName,
    userId,
    className,
    fallback,
}: Props) {
    const { ref, fit } = useSceneFit();
    const hasScene = !!content?.scenes?.[0] && !!metadata;

    return (
        <div
            ref={ref}
            className={cn(
                "relative w-full h-full overflow-hidden pointer-events-none select-none [&_*]:pointer-events-none [&_*]:select-none",
                className
            )}
            style={hasScene && !fit ? { visibility: "hidden" } : undefined}
        >
            {hasScene ? (
                <LomdaProvider
                    content={content!}
                    metadata={metadata!}
                    lomdaId={lomdaId}
                    lomdaName={lomdaName}
                    userId={userId}
                    readOnly
                    mode="render"
                >
                    <RichTextToolbarProvider>
                        <SceneCanvas />
                    </RichTextToolbarProvider>
                </LomdaProvider>
            ) : (
                (fallback ?? (
                    <Image
                        src="/images/image-placeholder.png"
                        alt="Lomda thumbnail"
                        fill
                        className="object-cover"
                    />
                ))
            )}
        </div>
    );
}
